// apps/dashboard/src/api/hooks/useBriefAnalysis.ts
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import type { Id } from "../../../../../convex/_generated/dataModel";
import type { HookState, BriefAnalysisFormState } from "../types";
import { useSubscriptionActive } from "../../components/layout/SubscriptionScope";
import { transformBriefAnalysis } from "../transforms/briefAnalysis";

// Hook contract:
//   - briefId === undefined  → { status: "empty" } (create-mode; no Convex round-trip)
//   - subscription scope inactive → { status: "loading" } (passes "skip" to useQuery)
//   - query in flight → { status: "loading" }
//   - query returns null (not found) → { status: "empty" }
//   - query returns { type: "forbidden" } → { status: "forbidden" }
//   - query returns { type: "ok", data } → { status: "ready", data: transformBriefAnalysis(data) }
//
// The hook never produces { status: "error" }. Convex platform errors during a query
// propagate through React's error-boundary path, where the route-level
// <ConvexErrorBoundary> catches them.
export function useBriefAnalysis(
  briefId: Id<"briefAnalyses"> | undefined,
): HookState<BriefAnalysisFormState> {
  const active = useSubscriptionActive();

  const raw = useQuery(
    api.briefAnalyses.getBrief,
    active && briefId !== undefined ? { briefId } : "skip",
  );

  if (briefId === undefined)        return { status: "empty" };
  if (!active)                      return { status: "loading" };
  if (raw === undefined)            return { status: "loading" };
  if (raw === null)                 return { status: "empty" };
  if (raw.type === "forbidden")     return { status: "forbidden" };
  return { status: "ready", data: transformBriefAnalysis(raw.data) };
}
